/**
 * BuoyMeshRegistry.ts — render glue between {@link Buoy} sim entities and
 * the GLB assets declared in {@link BuoyLibrary}.
 *
 * Per `BuoyKind` we register exactly one `StaticMeshPass` group; every
 * `Buoy` of that kind becomes one instance in that group. The instance
 * matrix is mutated each frame so the renderer sees the wave-driven bob.
 *
 * GLB authoring contract: **origin = waterline** in the modelled rest
 * pose (Blender Z=0 = world Y=0 in the simulator). The float is split
 * roughly half above / half below origin so a freshly placed model looks
 * naturally afloat without running physics. Real-world metres, +Z =
 * forward in Blender (export with default +Y Up → world Z forward).
 *
 * Mooring-line attachment happens at `Buoy.getWorldPos() = posY +
 * radiusY` (i.e. `radiusY` above the origin), which falls near the top
 * of the float where the cleat/topmark normally lives.
 */

import { loadGlb } from "../assets/GltfLoader";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Boat } from "./Boat";
import type { Buoy } from "./Buoy";
import type { BuoyKind, BuoyLibrary } from "./BuoyLibrary";
import { projectBodyPointOutsideBoatCollisionHull } from "./Collisions";

interface KindGroup {
  meshId: string;
}

interface InstanceRef {
  buoy: Buoy;
  meshId: string;
  matrix: Mat4;
}

export class BuoyMeshRegistry {
  private readonly groups = new Map<BuoyKind, KindGroup>();
  private readonly pendingLoads = new Map<BuoyKind, Promise<KindGroup>>();
  private readonly instances: InstanceRef[] = [];

  constructor(
    private readonly staticMeshPass: StaticMeshPass,
    private readonly library: BuoyLibrary,
  ) {}

  /**
   * Ensure a `StaticMeshPass` group exists for `kind`. Loads the GLB on
   * first request; subsequent calls return the cached group. Safe to
   * await concurrently — the second caller gets the same Promise.
   */
  async ensureKind(kind: BuoyKind): Promise<KindGroup> {
    const cached = this.groups.get(kind);
    if (cached) return cached;
    const pending = this.pendingLoads.get(kind);
    if (pending) return pending;
    const p = this.loadKind(kind).then((g) => {
      this.groups.set(kind, g);
      this.pendingLoads.delete(kind);
      return g;
    });
    this.pendingLoads.set(kind, p);
    return p;
  }

  private async loadKind(kind: BuoyKind): Promise<KindGroup> {
    const meshId = `buoy/${kind}`;
    const entry = this.library.tryGet(kind);
    if (!entry) {
      throw new Error(
        `BuoyMeshRegistry: kind "${kind}" not declared in public/buoys/index.json`,
      );
    }
    const mesh = await loadGlb(entry.url);
    this.staticMeshPass.addGroup(meshId, mesh, 0, {
      castsShadow: true,
      castsHardShadow: true,
      receivesShadow: true,
    });
    return { meshId };
  }

  /**
   * Register a sim {@link Buoy} so its visual mesh is added + tracked.
   * Returns the matrix backing the instance so the caller (or `update`)
   * can mutate it. Call `ensureKind(buoy.kind)` first.
   */
  addInstance(buoy: Buoy): Mat4 {
    const group = this.groups.get(buoy.kind);
    if (!group) {
      throw new Error(
        `BuoyMeshRegistry: addInstance("${buoy.kind}") before ensureKind`,
      );
    }
    // GLBs are authored with origin at the modelled waterline, so we
    // translate the model origin directly to `posY` (the ellipsoid
    // centre). In equilibrium `posY ≈ 0`, putting the origin exactly
    // on the world waterline.
    const m = makeMatrix(buoy);
    this.staticMeshPass.addInstance(group.meshId, m);
    this.instances.push({
      buoy,
      meshId: group.meshId,
      matrix: m,
    });
    return m;
  }

  /**
   * Refresh all instance matrices from their backing Buoy positions /
   * headings, and mark every touched group dirty so the GPU storage
   * buffers get re-uploaded this frame.
   */
  update(): void {
    if (this.instances.length === 0) return;
    const dirty = new Set<string>();
    for (const inst of this.instances) {
      writeMatrix(inst.matrix, inst.buoy);
      dirty.add(inst.meshId);
    }
    for (const id of dirty) this.staticMeshPass.markDirty(id);
  }

  /** Total number of buoy instances registered (across all kinds). */
  get instanceCount(): number {
    return this.instances.length;
  }
}

function makeMatrix(buoy: Buoy): Mat4 {
  const m = new Float32Array(16) as Mat4;
  writeMatrix(m, buoy);
  return m;
}

function writeMatrix(m: Mat4, buoy: Buoy): void {
  const c = Math.cos(buoy.headingY);
  const s = Math.sin(buoy.headingY);
  // Column-major: Y-up yaw rotation, translated to the buoy waterline
  // (= ellipsoid centre). GLBs are real-world scale so no per-axis
  // scaling — modeller is responsible for matching `radiusXZ/radiusY`.
  m[0] = c;
  m[1] = 0;
  m[2] = -s;
  m[3] = 0;
  m[4] = 0;
  m[5] = 1;
  m[6] = 0;
  m[7] = 0;
  m[8] = s;
  m[9] = 0;
  m[10] = c;
  m[11] = 0;
  m[12] = buoy.posX;
  m[13] = buoy.posY;
  m[14] = buoy.posZ;
  m[15] = 1;
}

/**
 * Push buoys out of the way of any boat that has overlapped them this
 * frame. Buoys NEVER block the boat (no collision AABBs are emitted) —
 * instead the boat sails right through and the buoy:
 *   - slides sideways out of the hull silhouette (free buoys only —
 *     anchored ones are pinned by their ground tackle); and
 *   - gets a downward velocity kick so it dips beneath the bow wave
 *     for a moment before bobbing back up.
 *
 * Should be called every frame after boat physics has integrated, and
 * before `buoyMeshRegistry.update()` so the mesh follows the new pose.
 *
 * @param boats The full list of moving + moored boats whose hulls can
 *   submerge buoys this frame. Static (immovable) boats still push.
 * @param buoys All sim buoys.
 * @param library Buoy entries (so we can skip non-solid markers).
 */
export function pushBuoysAwayFromBoats(
  boats: ReadonlyArray<Boat>,
  buoys: ReadonlyArray<Buoy>,
  library: BuoyLibrary,
): void {
  if (boats.length === 0 || buoys.length === 0) return;
  const probe: [number, number, number] = [0, 0, 0];
  for (const b of buoys) {
    const entry = library.tryGet(b.kind);
    if (entry && entry.collision.type === "none") continue;
    if (entry && entry.collision.solid === false) continue;
    for (const boat of boats) {
      // World → body frame (Y axes aligned).
      const ch = Math.cos(boat.heading);
      const sh = Math.sin(boat.heading);
      const fwdX = sh;
      const fwdZ = -ch;
      const rgtX = ch;
      const rgtZ = sh;
      const dx = b.posX - boat.posX;
      const dz = b.posZ - boat.posZ;
      probe[0] = dx * fwdX + dz * fwdZ;
      probe[1] = b.posY - boat.posY;
      probe[2] = dx * rgtX + dz * rgtZ;
      const originalBx = probe[0];
      const originalBz = probe[2];
      const projected = projectBodyPointOutsideBoatCollisionHull(boat, probe);
      if (!projected) continue;
      // Body-frame displacement that would push the buoy clear of the hull.
      const deltaBx = probe[0] - originalBx;
      const deltaBz = probe[2] - originalBz;
      // Body → world delta (no Y term — we want to keep the buoy on the
      // surface; sinking is driven by the velocity kick below).
      const dWorldX = deltaBx * fwdX + deltaBz * rgtX;
      const dWorldZ = deltaBx * fwdZ + deltaBz * rgtZ;
      // Closing speed of the hull point onto this buoy (m/s), used to
      // scale both the sideways shove and the dive depth.
      const closingSpeed = Math.hypot(boat.surge ?? 0, boat.sway ?? 0);
      if (!b.anchored) {
        // Free buoy: slide it clear and give a matching velocity kick so
        // it keeps drifting away instead of snapping back into the hull
        // on the next frame.
        b.posX += dWorldX;
        b.posZ += dWorldZ;
        const pen = Math.hypot(dWorldX, dWorldZ);
        if (pen > 1e-4) {
          const nx = dWorldX / pen;
          const nz = dWorldZ / pen;
          const kick = Math.max(0.6, closingSpeed * 0.6);
          b.vx += nx * kick;
          b.vz += nz * kick;
        }
      }
      // Both anchored and free: shove the buoy under the bow wave. A bit
      // of downward velocity (clamped by the boat's speed) makes it
      // submerge briefly and the spring-buoyancy in `Buoy.step` pops it
      // back up after the hull has passed.
      const submergeKick = Math.min(3.0, 0.8 + closingSpeed * 0.4);
      b.vy = Math.min(b.vy, -submergeKick);
    }
  }
}
